import 'react-tagsinput/react-tagsinput.css'

import { filter, findIndex, isArray, isEmpty, noop } from 'lodash'
import React from 'react'
import TagsInput from 'react-tagsinput'

import Field from '../Field'
import {
  defaultPasteSplit,
  defaultRenderInput,
  defaultRenderLayout,
} from './helpers'
import { TRenderTag, TTagInput } from './types'

const TagInput = (props: TTagInput): React.ReactElement => {
  const {
    name = 'tagInput',
    label = '',
    placeholder = undefined,
    showCondition = null,
    noInitialValue = false,
    required = false,
    disabled = false,
    validate = noop,
    hidePlaceholder = false,
    addKeys = [9, 13], // default is [9, 13] (Tab and Enter).
    currentValue = '',
    onlyUnique = false,
    validationRegex = /.*/,
    onValidationReject = noop,
    maxTags = -1, // default is infinite (-1)
    addOnBlur = true,
    addOnPaste = false,
    pasteSplit = defaultPasteSplit,
    removeKeys = [8], // default is [8] (Backspace)
    className = 'react-tagsinput',
    focusedClassName = 'react-tagsinput--focused',
    tagProps = {
      className: 'react-tagsinput-tag',
      classNameRemove: 'react-tagsinput-remove',
    },
    tagDisplayProp = null,
    renderTag,
    renderInput = defaultRenderInput,
    renderLayout = defaultRenderLayout,
    preventSubmit = true,
    withoutField,
    tooltip,
    excludeFromDeletion,
  } = props

  const fieldInputProps = {
    name,
    label,
    initialValue: undefined,
    showCondition,
    required,
    validate,
    tooltip,
  }

  const defaultRenderTag = (props: TRenderTag): React.ReactElement => {
    const {
      tag,
      key = 0,
      disabled,
      onRemove,
      classNameRemove,
      getTagDisplayValue,
      ...other
    } = props

    const excludeDelete =
      findIndex(excludeFromDeletion, (item) => item === tag) >= 0

    return (
      <span key={key} {...other}>
        {getTagDisplayValue(tag)}
        {!disabled && !excludeDelete && (
          <a className={`${classNameRemove}`} onClick={() => onRemove(key)} />
        )}
      </span>
    )
  }

  const tagsInputProps = {
    disabled,
    addOnBlur,
    inputProps: !hidePlaceholder
      ? {
          placeholder:
            placeholder || i18n.t('taginput_placeholder', '(Wert + Tab/Enter)'),
          className: 'react-tagsinput-input full-width-tag',
        }
      : {
          placeholder: null,
          className: 'react-tagsinput-input full-width-tag',
        },
    addKeys,
    currentValue,
    onlyUnique,
    validationRegex,
    onValidationReject,
    maxTags,
    addOnPaste,
    pasteSplit,
    removeKeys,
    className,
    focusedClassName,
    tagProps,
    tagDisplayProp,
    renderTag: isEmpty(renderTag) ? defaultRenderTag : renderTag,
    renderInput,
    renderLayout,
    preventSubmit,
  }

  const localOnchange = (
    value: string[],
    onChange: (val: string[]) => void,
  ): void => {
    const exludeDelete = filter(excludeFromDeletion, (item) => {
      return findIndex(value, (valueItem) => valueItem === item) === -1
    })
    onChange([...value, ...exludeDelete])
  }

  if (withoutField) {
    return (
      <>
        {label && label}
        <TagsInput
          {...tagsInputProps}
          onChange={(v) => localOnchange(v, props.onChange)}
          value={isArray(props.value) ? props.value : []}
        />
      </>
    )
  }

  return (
    <Field {...fieldInputProps}>
      {(fieldProps): React.ReactNode => {
        const { value, onChange } = fieldProps

        return (
          <TagsInput
            value={isArray(value) ? value : []}
            onChange={(val) => {
              localOnchange(val, onChange)
            }}
            {...tagsInputProps}
          />
        )
      }}
    </Field>
  )
}

export default TagInput
